import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Store, Plug, Warehouse, RefreshCw, Trash2, Copy, QrCode, FlaskConical, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChannelPricingRules, checkMarginGuard } from '@/components/ChannelPricingRules';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const DELIVERY_LABELS = { noon_courier: 'noon courier pickup', own_truck: 'Our truck delivery', express: 'Express (urgent self-delivery)' };

export default function NoonIntegrationPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [credsJson, setCredsJson] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [locations, setLocations] = useState([]);
  const [newMap, setNewMap] = useState({ noon_warehouse_code: '', app_location_id: '', delivery_type: 'noon_courier', stock_buffer: 0 });
  const [syncing, setSyncing] = useState(false);
  const [syncingOrders, setSyncingOrders] = useState(false);
  const [ordersFromDate, setOrdersFromDate] = useState('');
  const [allocating, setAllocating] = useState(false);
  const [sandboxWh, setSandboxWh] = useState('');
  const [pricing, setPricing] = useState(null);
  const [savingPricing, setSavingPricing] = useState(false);
  const [priceSyncing, setPriceSyncing] = useState(false);
  const [priceProgress, setPriceProgress] = useState(null);

  const load = async () => {
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/noon/status`, auth()),
        axios.get(`${API}/enterprise/locations`, auth()),
      ]);
      setStatus(s.data);
      const d = s.data || {};
      setPricing({
        price_markup_type: d.price_markup_type || 'none',
        price_markup_value: d.price_markup_value || '',
        shipping_fee_enabled: !!d.shipping_fee_enabled,
        shipping_fee_threshold: d.shipping_fee_threshold || '',
        shipping_fee_amount: d.shipping_fee_amount || '',
      });
      setLocations(l.data.locations || l.data || []);
    } catch (e) {
      toast.error('Failed to load noon status');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    let creds;
    try { creds = JSON.parse(credsJson); } catch { toast.error('Paste the full service account JSON from noon'); return; }
    if (!creds.key_id || !creds.private_key || !creds.project_code) { toast.error('JSON must contain key_id, private_key and project_code'); return; }
    setConnecting(true);
    try {
      const r = await axios.post(`${API}/noon/connect`, creds, auth());
      toast.success(`Connected to noon as ${r.data.username || 'partner'}`);
      setCredsJson('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connection failed');
    } finally {
      setConnecting(false);
    }
  };

  const addMapping = async () => {
    if (!newMap.noon_warehouse_code.trim() || !newMap.app_location_id) { toast.error('Enter the noon warehouse code and pick an app warehouse'); return; }
    try {
      const r = await axios.post(`${API}/noon/mappings`, { ...newMap, stock_buffer: parseInt(newMap.stock_buffer) || 0 }, auth());
      toast.success('Warehouse mapped');
      if (r.data.mapping?.warehouse_valid === false) toast.warning('noon did not recognize this warehouse code — double-check it in Seller Lab');
      setNewMap({ noon_warehouse_code: '', app_location_id: '', delivery_type: 'noon_courier', stock_buffer: 0 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add mapping');
    }
  };

  const deleteMapping = async (id) => {
    try { await axios.delete(`${API}/noon/mappings/${id}`, auth()); toast.success('Mapping removed'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const syncOrders = async () => {
    setSyncingOrders(true);
    try {
      const r = await axios.post(`${API}/noon/sync-orders`, ordersFromDate ? { from_date: ordersFromDate } : {}, auth());
      toast.success(r.data.message || 'noon orders synced');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Order sync failed');
    } finally {
      setSyncingOrders(false);
    }
  };

  const toggleMapping = async (m) => {
    try { await axios.put(`${API}/noon/mappings/${m.id}`, { enabled: !m.enabled }, auth()); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const savePricing = async () => {
    setSavingPricing(true);
    try {
      await axios.put(`${API}/noon/settings`, {
        price_markup_type: pricing.price_markup_type || 'none',
        price_markup_value: parseFloat(pricing.price_markup_value) || 0,
        shipping_fee_enabled: !!pricing.shipping_fee_enabled,
        shipping_fee_threshold: parseFloat(pricing.shipping_fee_threshold) || 0,
        shipping_fee_amount: parseFloat(pricing.shipping_fee_amount) || 0,
      }, auth());
      toast.success('Pricing rules saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setSavingPricing(false); }
  };

  const syncPrices = async () => {
    setPriceSyncing(true);
    setPriceProgress(null);
    try {
      const guard = await checkMarginGuard('noon');
      if (!guard.proceed) { setPriceSyncing(false); return; }
      const start = await axios.post(`${API}/noon/sync-prices`, { ignore_margin_guard: guard.ignore }, auth());
      const jobId = start.data.job_id;
      for (let i = 0; i < 200; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const jr = await axios.get(`${API}/noon/price-job/${jobId}`, auth());
        const job = jr.data;
        if (job.progress) setPriceProgress(job.progress);
        if (job.status === 'running') continue;
        if (job.status === 'failed') {
          toast.error(job.error || 'Price sync failed');
        } else {
          const res = job.result || {};
          if (res.failed_count) {
            const firstMsg = (res.failed || [])[0]?.message || '';
            toast.warning(`Pushed ${res.pushed || 0}, ${res.failed_count} failed — ${firstMsg}`);
          } else {
            toast.success(`Prices pushed — ${res.pushed || 0} update(s) to noon${res.blocked_below_cost ? `, ${res.blocked_below_cost} blocked below cost` : ''}`);
          }
        }
        setPriceSyncing(false);
        setPriceProgress(null);
        return;
      }
      toast.info('Price sync is still running in the background');
    } catch (e) { toast.error(e.response?.data?.detail || 'Price sync failed'); }
    finally { setPriceSyncing(false); setPriceProgress(null); }
  };

  const syncStock = async (mappingId = null) => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/noon/sync-stock`, mappingId ? { mapping_id: mappingId } : {}, auth());
      const res = r.data.results || [];
      const pushed = res.reduce((s, x) => s + x.pushed, 0);
      const failed = res.reduce((s, x) => s + x.failed_count, 0);
      if (failed > 0) {
        const firstMsg = res.flatMap(x => x.failed || [])[0]?.message || 'unknown error';
        toast.warning(`Synced ${pushed} SKU(s), ${failed} failed — ${String(firstMsg).split('\n').pop()}`);
      } else {
        toast.success(`Stock synced — ${pushed} SKU(s) pushed to noon`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const allocateAwbs = async () => {
    setAllocating(true);
    try {
      const r = await axios.post(`${API}/noon/awbs/allocate`, { count: 50 }, auth());
      toast.success(`${r.data.allocated} AWB numbers allocated (pool: ${r.data.pool_free})`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AWB allocation failed');
    } finally {
      setAllocating(false);
    }
  };

  const createSandbox = async () => {
    if (!sandboxWh.trim()) { toast.error('Enter a noon warehouse code for the test order'); return; }
    try {
      const r = await axios.post(`${API}/noon/sandbox-order`, { warehouse_code: sandboxWh.trim() }, auth());
      toast.success(`Sandbox order ${r.data.fbpi_order_nr} created — check the Orders page`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sandbox order failed');
    }
  };

  const webhookUrl = status?.webhook_token ? `${process.env.REACT_APP_BACKEND_URL}/api/noon/webhook/${status.webhook_token}` : null;

  if (loading) return <div className="p-6 text-wt-text-muted">Loading…</div>;

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6" data-testid="noon-integration-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/settings')} className="text-wt-text-muted" data-testid="noon-back-btn">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
          <Store className="h-6 w-6 text-yellow-400" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-wt-text-primary">noon Marketplace</h1>
          <p className="text-sm text-wt-text-secondary">FBPI orders, shipments & per-warehouse inventory sync</p>
        </div>
        {status?.connected && <Badge className="ml-auto bg-green-500/20 text-green-400 border border-green-500/30" data-testid="noon-connected-badge">Connected{status.username ? ` — ${status.username.split('@')[0]}` : ''}</Badge>}
      </div>

      {/* Connection */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-wt-paper border border-wt-border rounded-xl p-6 space-y-3">
        <h2 className="text-lg font-semibold text-wt-text-primary flex items-center gap-2"><Plug className="h-5 w-5 text-yellow-400" /> Connection</h2>
        {!status?.connected ? (
          <>
            <p className="text-sm text-wt-text-muted">Paste the service account JSON downloaded from <b>access.noon.partners → API access</b>.</p>
            <Textarea rows={5} placeholder='{"key_id": "...", "private_key": "-----BEGIN PRIVATE KEY-----...", "project_code": "PRJ..."}'
              value={credsJson} onChange={(e) => setCredsJson(e.target.value)}
              className="bg-wt-bg border-wt-border text-wt-text-primary font-mono text-xs" data-testid="noon-credentials-input" />
            <Button onClick={connect} disabled={connecting} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="noon-connect-btn">
              {connecting ? 'Connecting…' : 'Connect to noon'}
            </Button>
          </>
        ) : (
          <>
            {webhookUrl && (
              <div>
                <Label className="text-xs text-wt-text-muted">Order webhook URL — paste this in each Integration Warehouse in noon Seller Lab</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input readOnly value={webhookUrl} className="bg-wt-bg border-wt-border text-wt-text-primary text-xs" data-testid="noon-webhook-url-input" />
                  <Button variant="outline" size="sm" className="border-wt-border text-wt-text-primary shrink-0" data-testid="noon-copy-webhook-btn"
                    onClick={async () => { try { await navigator.clipboard.writeText(webhookUrl); toast.success('Webhook URL copied'); } catch { toast.error('Copy failed — select and copy manually'); } }}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            <div className="flex items-center gap-4 text-sm text-wt-text-muted">
              <span>AWB pool: <b className="text-wt-text-primary" data-testid="noon-awb-pool-count">{status.awb_pool_free}</b> free</span>
              <span>Pending manifest: <b className="text-amber-400" data-testid="noon-pending-manifest-count">{status.pending_manifest}</b></span>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={allocateAwbs} disabled={allocating} className="border-wt-border text-wt-text-primary" data-testid="noon-allocate-awbs-btn">
                <QrCode className="h-4 w-4 mr-1" /> {allocating ? 'Allocating…' : 'Allocate 50 AWBs'}
              </Button>
              <input type="date" value={ordersFromDate} onChange={(e) => setOrdersFromDate(e.target.value)}
                title="Only sync orders placed on or after this date"
                className="h-9 px-2 rounded-md bg-wt-bg border border-wt-border text-wt-text-primary text-xs"
                data-testid="noon-orders-from-date" />
              <Button variant="outline" size="sm" onClick={syncOrders} disabled={syncingOrders} className="border-wt-border text-wt-text-primary" data-testid="noon-sync-orders-btn">
                <RefreshCw className={`h-4 w-4 mr-1 ${syncingOrders ? 'animate-spin' : ''}`} /> {syncingOrders ? 'Syncing…' : 'Sync Orders Now'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate('/settings/noon/publish')} className="border-wt-border text-wt-text-primary" data-testid="noon-publish-products-btn">
                List Products on noon
              </Button>
              <Button variant="outline" size="sm"
                onClick={() => {
                  if (!window.confirm('Disconnect noon? The stored credentials are deleted and you will need to paste the service account JSON again.')) return;
                  axios.post(`${API}/noon/disconnect`, {}, auth())
                    .then(() => { toast.success('Disconnected'); load(); })
                    .catch((e) => toast.error(e.response?.data?.detail || 'Failed to disconnect'));
                }}
                className="border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid="noon-disconnect-btn">Disconnect</Button>
            </div>
          </>
        )}
      </motion.div>

      {/* Price sync */}
      {status?.connected && pricing && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-wt-paper border border-wt-border rounded-xl p-6 space-y-4" data-testid="noon-pricing-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-lg font-semibold text-wt-text-primary flex items-center gap-2"><Tag className="h-5 w-5 text-yellow-400" /> Price Sync — Price &amp; MSRP</h2>
            <Button size="sm" onClick={syncPrices} disabled={priceSyncing} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="noon-sync-prices-btn">
              <RefreshCw className={`h-4 w-4 mr-1 ${priceSyncing ? 'animate-spin' : ''}`} /> Sync Prices to noon Now
            </Button>
          </div>
          <p className="text-xs text-wt-text-muted">Your <b>compare-at price</b> goes to noon's <b>MSRP</b> (the crossed-out price) and your selling price becomes the noon <b>Price</b>. Products without a compare-at price send the price only.</p>
          {priceSyncing && (
            <div className="rounded-lg border border-wt-gold/30 bg-wt-gold/5 p-3 space-y-2" data-testid="noon-price-progress">
              <div className="flex items-center justify-between text-xs">
                <span className="text-wt-text-primary">
                  {priceProgress ? `Pushing ${priceProgress.done}/${priceProgress.total} updates — ${priceProgress.pushed} ok${priceProgress.failed ? `, ${priceProgress.failed} failed` : ''}` : 'Preparing price updates…'}
                </span>
                <span className="text-wt-gold font-semibold" data-testid="noon-price-progress-pct">
                  {priceProgress && priceProgress.total ? `${Math.round((priceProgress.done / priceProgress.total) * 100)}%` : ''}
                </span>
              </div>
              <div className="h-2 rounded-full bg-wt-bg overflow-hidden">
                <div className="h-full rounded-full bg-wt-gold transition-all duration-500"
                  style={{ width: priceProgress && priceProgress.total ? `${Math.max(4, Math.round((priceProgress.done / priceProgress.total) * 100))}%` : '4%' }} />
              </div>
            </div>
          )}
          <ChannelPricingRules pricing={pricing} setPricing={setPricing} idPrefix="noon" priceLabel="Price" compareLabel="MSRP" />
          <Button size="sm" onClick={savePricing} disabled={savingPricing} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="noon-save-pricing-btn">
            {savingPricing ? 'Saving…' : 'Save Pricing Rules'}
          </Button>
        </motion.div>
      )}

      {/* Warehouse mappings */}
      {status?.connected && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-wt-paper border border-wt-border rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-lg font-semibold text-wt-text-primary flex items-center gap-2"><Warehouse className="h-5 w-5 text-yellow-400" /> Warehouse Mappings</h2>
            <Button size="sm" onClick={() => syncStock()} disabled={syncing || !(status.mappings || []).some(m => m.enabled)} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="noon-sync-all-btn">
              <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? 'animate-spin' : ''}`} /> Sync All Stock Now
            </Button>
          </div>
          <p className="text-xs text-wt-text-muted">Each noon warehouse pushes the mapped app warehouse's available stock (minus buffer). Auto-sync runs on every stock change (within ~2 min) plus an hourly full reconciliation.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 items-end">
            <div>
              <Label className="text-xs text-wt-text-muted">noon warehouse code</Label>
              <Input placeholder="e.g. WH-NOON-001" value={newMap.noon_warehouse_code}
                onChange={(e) => setNewMap(p => ({ ...p, noon_warehouse_code: e.target.value }))}
                className="bg-wt-bg border-wt-border text-wt-text-primary" data-testid="noon-warehouse-code-input" />
            </div>
            <div>
              <Label className="text-xs text-wt-text-muted">App warehouse</Label>
              <Select value={newMap.app_location_id} onValueChange={(v) => setNewMap(p => ({ ...p, app_location_id: v }))}>
                <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary" data-testid="noon-app-warehouse-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-wt-text-muted">Delivery type</Label>
              <Select value={newMap.delivery_type} onValueChange={(v) => setNewMap(p => ({ ...p, delivery_type: v }))}>
                <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary" data-testid="noon-delivery-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  <SelectItem value="noon_courier">noon courier pickup</SelectItem>
                  <SelectItem value="own_truck">Our truck (bulky)</SelectItem>
                  <SelectItem value="express">Express self-delivery</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-wt-text-muted">Stock buffer</Label>
              <Input type="number" value={newMap.stock_buffer} onChange={(e) => setNewMap(p => ({ ...p, stock_buffer: e.target.value }))}
                className="bg-wt-bg border-wt-border text-wt-text-primary" data-testid="noon-stock-buffer-input" />
            </div>
            <Button onClick={addMapping} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="noon-add-mapping-btn">Add Mapping</Button>
          </div>

          <div className="space-y-2">
            {(status.mappings || []).length === 0 ? (
              <p className="text-wt-text-muted text-sm text-center py-4" data-testid="noon-no-mappings">No warehouse mappings yet</p>
            ) : (status.mappings || []).map(m => (
              <div key={m.id} className="flex items-center justify-between gap-3 p-3 bg-wt-bg rounded-lg border border-wt-border flex-wrap" data-testid={`noon-mapping-${m.noon_warehouse_code}`}>
                <div className="min-w-0">
                  <p className="text-sm text-wt-text-primary"><b>{m.noon_warehouse_code}</b> → {m.app_location_name}</p>
                  <p className="text-xs text-wt-text-muted">{DELIVERY_LABELS[m.delivery_type] || m.delivery_type}{m.stock_buffer ? ` · buffer ${m.stock_buffer}` : ''}{m.warehouse_valid === false ? ' · ⚠ code not recognized by noon' : ''}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="outline" size="sm" className="border-wt-border text-wt-text-primary" onClick={() => syncStock(m.id)} disabled={syncing || !m.enabled} data-testid={`noon-sync-mapping-${m.noon_warehouse_code}`}>
                    <RefreshCw className="h-3 w-3" />
                  </Button>
                  <Switch checked={m.enabled !== false} onCheckedChange={() => toggleMapping(m)} data-testid={`noon-toggle-mapping-${m.noon_warehouse_code}`} />
                  <Button variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/10" onClick={() => deleteMapping(m.id)} data-testid={`noon-delete-mapping-${m.noon_warehouse_code}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {status.last_sync && (
            <p className="text-xs text-wt-text-muted">
              Last sync ({status.last_sync.trigger}) at {new Date(status.last_sync.created_at).toLocaleString()}: {(status.last_sync.results || []).map(r => `${r.warehouse_code}: ${r.pushed}/${r.total} pushed${r.failed_count ? `, ${r.failed_count} failed` : ''}`).join(' · ')}
            </p>
          )}
        </motion.div>
      )}

      {/* Sandbox test */}
      {status?.connected && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-wt-paper border border-wt-border rounded-xl p-6 space-y-3">
          <h2 className="text-lg font-semibold text-wt-text-primary flex items-center gap-2"><FlaskConical className="h-5 w-5 text-yellow-400" /> Test Order (Sandbox)</h2>
          <p className="text-xs text-wt-text-muted">Creates a noon sandbox order against a warehouse — no real customer involved. The order will appear on the Orders page tagged "noon".</p>
          <div className="flex gap-2">
            <Input placeholder="noon warehouse code" value={sandboxWh} onChange={(e) => setSandboxWh(e.target.value)}
              className="bg-wt-bg border-wt-border text-wt-text-primary" data-testid="noon-sandbox-warehouse-input" />
            <Button onClick={createSandbox} variant="outline" className="border-wt-border text-wt-text-primary shrink-0" data-testid="noon-sandbox-create-btn">Create Test Order</Button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
